import React, { Component } from 'react';
import { Flex, Button, TextField, RadioButton, Dropdown } from 'monday-ui-react-core';
import { Heading } from "monday-ui-react-core/next";
import classes from "./encryption-wizard.module.scss";
import { showNotice } from '../../services/error-handling';
import mondaySdk from "monday-sdk-js";
import { getItem, updateColumnValue } from '../../services/api-service';
import { encrypt, decrypt } from '../../services/crypto-service';
import { valueCreated } from '../../services/hooks';

class EncryptionWizard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      item: null,
      operationType: "encrypt",
      boardId: null,
      pulseId: null,
      userId: null,
      columns: [],
      selectedColumnId: null,
      secret: "",
      plainValue: "",
      processedValue: "",
      processing: false,
      applyLoading: false,
      isInitializing: true,
      showSecret: false,
      showPlainValue: false,
      showDecryptedValue: false
    };
    this.monday = mondaySdk();
  }

  componentDidMount() {
    this.monday.listen("context", async (res) => {
      const context = res.data || {};

      this.setState({
        userId: context.user?.id ?? null,
        boardId: context.boardId ?? null,
        pulseId: context.itemId ?? null
      });

      await this.loadItem(context.itemId);
    });
  }

  loadItem = async (itemId) => {
    try {
      const item = await getItem(itemId);
      const columns = (item?.column_values || [])
        .filter((column) => column?.column?.title && ["text", "long_text"].includes(column?.type))
        .map((column) => ({
          label: column.column.title,
          value: column.id,
          text: column.text ?? "",
          type: column.type
        }));

      this.setState((prevState) => {
        const selectedColumnId = columns.length ? (prevState.selectedColumnId && columns.some((col) => col.value === prevState.selectedColumnId)
          ? prevState.selectedColumnId
          : columns[0].value) : null;

        return {
          item,
          columns,
          selectedColumnId,
          isInitializing: false,
          plainValue: "",
          processedValue: "",
          processing: false
        };
      });
    } catch (error) {
      console.error(error);
      this.setState({ isInitializing: false });
      showNotice("error", "Failed to load the item data. Please refresh and try again.");
    }
  };

  getSelectedColumn = () => {
    const { columns, selectedColumnId } = this.state;
    return columns.find((column) => column.value === selectedColumnId);
  };

  handleOperationChange = (operationType) => {
    this.setState((prevState) => {
      let nextSelectedColumnId = prevState.selectedColumnId;
      if (
        operationType === "decrypt" &&
        (!nextSelectedColumnId || !prevState.columns.some((column) => column.value === nextSelectedColumnId))
      ) {
        nextSelectedColumnId = prevState.columns[0]?.value ?? null;
      }

      return {
        operationType,
        processedValue: "",
        processing: false,
        plainValue: "",
        selectedColumnId: nextSelectedColumnId
      };
    });
  };

  handleDecryptColumnChange = (selectedOption) => {
    this.setState({
      selectedColumnId: selectedOption ? selectedOption.value : null,
      processedValue: "",
      processing: false
    });
  };

  handleDestinationColumnChange = (selectedOption) => {
    this.setState({
      selectedColumnId: selectedOption ? selectedOption.value : null
    });
  };

  handleSecretChange = (value) => {
    this.setState({
      secret: value,
      processedValue: "",
      processing: false
    });
  };

  handlePlainValueChange = (value) => {
    this.setState({
      plainValue: value,
      processedValue: "",
      processing: false
    });
  };

  toggleSecretVisibility = () => {
    this.setState((prevState) => ({
      showSecret: !prevState.showSecret
    }));
  };

  togglePlainValueVisibility = () => {
    this.setState((prevState) => ({
      showPlainValue: !prevState.showPlainValue
    }));
  };

  toggleDecryptedVisibility = () => {
    this.setState((prevState) => ({
      showDecryptedValue: !prevState.showDecryptedValue
    }));
  };

  renderVisibilityIcon = (isVisible) => (
    <svg className={classes.iconSvg} viewBox="0 0 24 24" role="img" aria-hidden="true">
      <path
        d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 12c-2.761 0-5-2.239-5-5s2.239-5 5-5 5 2.239 5 5-2.239 5-5 5zm0-8c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3z"
        fill="currentColor"
      />
      {!isVisible && (
        <line
          x1="4"
          y1="4"
          x2="20"
          y2="20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );

  renderVisibilityToggle = (isVisible, onToggle, label) => (
    <button
      type="button"
      className={classes.iconToggleButton}
      onClick={onToggle}
      aria-label={`${isVisible ? "Hide" : "Show"} ${label}`}
    >
      {this.renderVisibilityIcon(isVisible)}
    </button>
  );

  handleProcess = async () => {
    const { operationType, secret, plainValue } = this.state;
    const isEncrypt = operationType === "encrypt";
    const selectedColumn = this.getSelectedColumn();

    const normalizedSecret = secret.trim();
    const normalizedPlainValue = plainValue.trim();

    if (!normalizedSecret) {
      showNotice("error", "Please provide a secret to continue.");
      return;
    }

    let sourceValue = "";
    if (isEncrypt) {
      if (!normalizedPlainValue) {
        showNotice("error", "Please enter a value to encrypt.");
        return;
      }
      sourceValue = normalizedPlainValue;
    } else {
      if (!selectedColumn) {
        showNotice("error", "Please select a column first.");
        return;
      }
      sourceValue = typeof selectedColumn.text === "string" ? selectedColumn.text : "";
      if (!sourceValue) {
        showNotice("error", "Selected column does not contain an encrypted value.");
        return;
      }
    }

    this.setState({
      processing: true,
      processedValue: ""
    });

    try {
      const processedValue = isEncrypt
        ? await encrypt(normalizedSecret, sourceValue)
        : await decrypt(normalizedSecret, sourceValue);

      valueCreated();

      this.setState({
        processedValue,
        processing: false
      });
    } catch (error) {
      console.error(error);
      this.setState({
        processing: false,
        processedValue: ""
      });
      showNotice("error", error.message || "Unable to process the column value.");
    }
  };

  handleApplyValue = async () => {
    const { processedValue, selectedColumnId, boardId, pulseId } = this.state;
    if (!processedValue) {
      showNotice("error", "Please encrypt the value before applying it to the column.");
      return;
    }

    if (!selectedColumnId) {
      showNotice("error", "Please select a column to update.");
      return;
    }

    if (!boardId || !pulseId) {
      showNotice("error", "Missing required context to update the column.");
      return;
    }

    this.setState({
      applyLoading: true
    });

    try {
      await updateColumnValue(boardId, pulseId, selectedColumnId, processedValue);
      this.setState((prevState) => ({
        applyLoading: false,
        columns: prevState.columns.map((column) => {
          if (column.value === selectedColumnId) {
            return {
              ...column,
              text: processedValue
            };
          }
          return column;
        })
      }));
      showNotice("success", "Column updated successfully.");
    } catch (error) {
      console.error(error);
      this.setState({
        applyLoading: false
      });
      showNotice("error", "Failed to update the column. Please try again.");
    }
  };

  handleCopyValue = async () => {
    const { processedValue } = this.state;
    if (!processedValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(processedValue);
      showNotice("success", "Decrypted value copied to clipboard.");
    } catch (error) {
      console.error(error);
      showNotice("error", "Unable to copy the value. Please copy it manually.");
    }
  };

  render() {
    const {
      operationType,
      columns,
      selectedColumnId,
      secret,
      plainValue,
      processedValue,
      processing,
      applyLoading,
      isInitializing,
      showSecret,
      showPlainValue,
      showDecryptedValue
    } = this.state;

    const isEncrypt = operationType === "encrypt";
    const hasSecret = !!secret;
    const isActionDisabled = isInitializing
      || processing
      || !hasSecret
      || (isEncrypt ? !plainValue : !selectedColumnId);

    const selectedColumnOption = columns.find((column) => column.value === selectedColumnId);
    const operationButtonLabel = operationType === "encrypt" ? "Encrypt value" : "Decrypt value";

    return (
      <Flex align={Flex.align.START} gap={Flex.gaps.MEDIUM} direction={Flex.directions.COLUMN} className={classes.container}>
        <div className={classes.warningNote}>
          Do not paste secrets into columns in plaintext — board version history keeps every value and exposes sensitive data.
        </div>
        <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
          <div className={classes.formTitle}>Select operation:</div>
          <RadioButton
            text="Encrypt"
            name="operation-type-group"
            defaultChecked={operationType === "encrypt"}
            onSelect={() => this.handleOperationChange("encrypt")}
          />
          <RadioButton
            text="Decrypt"
            name="operation-type-group"
            defaultChecked={operationType === "decrypt"}
            onSelect={() => this.handleOperationChange("decrypt")}
          />
        </Flex>
        <div className={classes.fieldNote}>
          Choose whether to encrypt a new value or decrypt an existing column value.
        </div>
        {operationType === "decrypt" && (
          <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
            <div className={classes.formTitle}>Encrypted column source</div>
            <Dropdown
              className={classes.inputField}
              placeholder={isInitializing ? "Loading columns..." : "Select column"}
              options={columns}
              value={selectedColumnOption || null}
              disabled={isInitializing}
              onChange={this.handleDecryptColumnChange}
            />
            <div className={classes.fieldNote}>
              Choose the column that holds the encrypted text you want to decrypt.
            </div>
          </Flex>
        )}
        <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
          <div className={classes.formTitle}>Encryption secret (shared key)</div>
          <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW}>
            <TextField
              size={TextField.sizes.MEDIUM}
              value={secret}
              type={showSecret ? TextField.types.TEXT : TextField.types.PASSWORD}
              onChange={this.handleSecretChange}
              disabled={isInitializing}
              className={classes.inputField}
            />
            {this.renderVisibilityToggle(showSecret, this.toggleSecretVisibility, "secret")}
          </Flex>
          <div className={classes.fieldNote}>
            {isEncrypt
              ? "Provide the secret key that will encrypt this value. You'll need the same key to decrypt it later."
              : "Enter the exact secret key that was used when encrypting the column value."}
          </div>
        </Flex>
        {operationType === "encrypt" && (
          <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
            <div className={classes.formTitle}>Plaintext value to encrypt</div>
            <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW}>
              <TextField
                size={TextField.sizes.MEDIUM}
                value={plainValue}
                type={showPlainValue ? TextField.types.TEXT : TextField.types.PASSWORD}
                onChange={this.handlePlainValueChange}
                disabled={isInitializing}
                className={classes.inputField}
              />
              {this.renderVisibilityToggle(showPlainValue, this.togglePlainValueVisibility, "plaintext value")}
            </Flex>
            <div className={classes.fieldNote}>
              Provide the sensitive information you want to protect.
            </div>
          </Flex>
        )}
        <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.PRIMARY}
            loading={processing}
            disabled={isActionDisabled}
            onClick={this.handleProcess}
          >
            {operationButtonLabel}
          </Button>
        </Flex>
        {(operationType === "encrypt" && processedValue) && (
          <>
            <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
              <div className={classes.formTitle}>Generated encrypted value</div>
              <TextField
                size={TextField.sizes.MEDIUM}
                value={processedValue}
                maxLength={10000}
                readOnly
                onChange={() => {}}
                className={classes.inputField}
              />
              <div className={classes.fieldNote}>
                Review or copy this ciphertext before applying it to a board column.
              </div>
            </Flex>
            <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
              <div className={classes.formTitle}>Destination column for encrypted value</div>
              <Dropdown
                className={classes.inputField}
                placeholder="Select column"
                options={columns}
                value={selectedColumnOption || null}
                onChange={this.handleDestinationColumnChange}
              />
              <div className={classes.fieldNote}>
                Select where the encrypted text should be stored on the item.
              </div>
            </Flex>
            <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW}>
              <Button
                size={Button.sizes.SMALL}
                kind={Button.kinds.SECONDARY}
                loading={applyLoading}
                disabled={applyLoading || !processedValue || !selectedColumnId}
                onClick={this.handleApplyValue}
              >
                Apply value to column
              </Button>
            </Flex>
          </>
        )}
        {(operationType === "decrypt" && processedValue) && (
          <Flex align={Flex.align.START} gap={Flex.gaps.SMALL} direction={Flex.directions.COLUMN} className={classes.fullWidth}>
            <div className={classes.formTitle}>Decrypted plaintext value</div>
            <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL} direction={Flex.directions.ROW} className={classes.fullWidth}>
              <TextField
                size={TextField.sizes.MEDIUM}
                value={processedValue}
                type={showDecryptedValue ? TextField.types.TEXT : TextField.types.PASSWORD}
                readOnly
                className={classes.inputField}
                onChange={() => {}}
              />
              {this.renderVisibilityToggle(showDecryptedValue, this.toggleDecryptedVisibility, "decrypted value")}
              <Button
                size={Button.sizes.SMALL}
                kind={Button.kinds.TERTIARY}
                disabled={!processedValue}
                onClick={this.handleCopyValue}
              >
                Copy to clipboard
              </Button>
            </Flex>
            <div className={classes.fieldNote}>
              Copy the decrypted value to view it elsewhere; it remains masked here for security.
            </div>
          </Flex>
        )}
      </Flex>
    );
  }
}

export default EncryptionWizard;
